import { useResourcesStore } from '@/store/modules/resources'

const defaultLogoSvg = `<?xml version="1.0" encoding="UTF-8" standalone="no"?>
<svg width="58.976822" height="61.550301" viewBox="0 0 58.976822 61.5503" version="1.1" id="svg2" xmlns="http://www.w3.org/2000/svg">
  <g fill="#caff01" transform="matrix(0.69243971,0,0,0.69243971,-5.1330706,-3.8479092)">
    <path d="M 65.309,5.5586 C 60.9535,5.613287 56.6293,6.15235 52.418,7.1524 42.051,9.6212 32.359,14.8946 24.551,22.6994 8.977,38.3044 3.477,61.3794 10.289,82.4764 c 0.0039,0.01953 0.01172,0.03516 0.01563,0.05469 0.06641,0.20312 0.16016,0.39844 0.26953,0.58203 0.02734,0.03906 0.05078,0.08203 0.09766,0.12109 -4.1367,4.0117 -6.7109,9.6211 -6.7109,15.812 0,6.25 2.6211,11.91 6.8242,15.934 0.0078,0.0078 0.01563,0.01563 0.02344,0.02344 3.9609,3.7812 9.3164,6.1094 15.195,6.1094 5.7383,0 10.98,-2.2148 14.906,-5.8359 v -0.0039 c 0.11719,-0.08984 0.22656,-0.1875 0.32422,-0.29688 0.03906,-0.03516 0.07422,-0.07422 0.10938,-0.11328 4.1289,-4.0156 6.6992,-9.625 6.6992,-15.812 0,-6.168 -2.5547,-11.754 -6.6562,-15.762 -0.04688,-0.05859 -0.09766,-0.11719 -0.15234,-0.17188 -0.11328,-0.11719 -0.23438,-0.22656 -0.36719,-0.32031 -3.9258,-3.5938 -9.1445,-5.7891 -14.863,-5.7891 z m 0,5.5547 c 3.4062,0 6.5625,1.0234 9.1836,2.7773 -3.082,3.875 -4.7969,8.6992 -4.7969,13.711 0,5.0195 1.707,9.8477 4.7891,13.727 -2.6211,1.7578 -5.7734,2.7773 -9.1797,2.7773 -3.4023,0 -6.5586,-1.0234 -9.1797,-2.7773 3.082,-3.8789 4.7891,-8.7109 4.7891,-13.727 0,-5.0156 -1.7031,-9.8438 -4.7852,-13.719 2.6172,-1.75 5.7734,-2.7656 9.1719,-2.7656 z m -57.512,5.6953 c 3.875,2.5938 7.5742,5.6328 10.965,9.0273 3.3945,3.3984 6.4453,7.0977 9.043,10.977 -6.0039,0.29297 -12.043,-0.42188 -17.863,-2.1445 -1.7383,-5.8906 -2.4336,-11.926 -2.1445,-17.859 z m 44.289,0.91797 c 2.1133,2.8281 3.2773,6.2852 3.2773,9.875 0,3.5898 -1.1641,7.0469 -3.2812,9.8789 -2.0508,-2.75 -3.2617,-6.168 -3.2617,-9.8789 0,-3.7148 1.2148,-7.125 3.2656,-9.875 z m 26.453,0.01172 c 2.0469,2.7461 3.2539,6.1562 3.2539,9.8633 0,3.7109 -1.2109,7.1289 -3.2617,9.8789 -2.1172,-2.832 -3.2812,-6.2891 -3.2812,-9.8789 0,-3.582 1.1719,-7.0352 3.2891,-9.8633 z" /></g>
</svg>`

const defaultLogoUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(defaultLogoSvg).replace(/'/g, '%27').replace(/"/g, '%22')}`

export interface LogoData {
  context?: string
  background?: [number, number, number] | string
  cornerRadius?: number
  width?: number
  height?: number
}

const iconCache = new Map<string, string>()

export async function useBrandIcon(logoData?: LogoData): Promise<string> {
  const resourcesStore = useResourcesStore()
  const context = logoData?.context?.toLowerCase()
  const width = logoData?.width ?? 256
  const height = logoData?.height ?? 256
  const cornerRadius = logoData?.cornerRadius ?? Math.min(width, height) / 6

  const bgArray = logoData?.background
  const isRgbArray = Array.isArray(bgArray) && bgArray.length === 3
  const bgColor = isRgbArray
    ? `rgb(${bgArray[0]}, ${bgArray[1]}, ${bgArray[2]})`
    : typeof bgArray === 'string'
      ? bgArray
      : 'rgb(255,255,255)'

  const cacheKey = JSON.stringify({ context, bgColor, cornerRadius, width, height })

  if (iconCache.has(cacheKey)) {
    return iconCache.get(cacheKey)!
  }

  // Get SVG content from resources store
  let svgContent: string | undefined
  if (context) {
    svgContent = resourcesStore.getIconSvg(`${context}.svg`) || resourcesStore.getBannerSvg(`${context}.svg`)
  }

  let iconUrl: string
  if (!svgContent) {
    iconUrl = defaultLogoUrl
  } else {
    const encoded = encodeURIComponent(svgContent)
      .replace(/'/g, '%27')
      .replace(/"/g, '%22')
    iconUrl = `data:image/svg+xml;charset=UTF-8,${encoded}`
  }

  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')!
      canvas.width = width
      canvas.height = height

      ctx.clearRect(0, 0, width, height)

      function drawRoundedRect(x: number, y: number, w: number, h: number, r: number) {
        ctx.beginPath()
        ctx.moveTo(x + r, y)
        ctx.lineTo(x + w - r, y)
        ctx.quadraticCurveTo(x + w, y, x + w, y + r)
        ctx.lineTo(x + w, y + h - r)
        ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
        ctx.lineTo(x + r, y + h)
        ctx.quadraticCurveTo(x, y + h, x, y + h - r)
        ctx.lineTo(x, y + r)
        ctx.quadraticCurveTo(x, y, x + r, y)
        ctx.closePath()
      }

      ctx.save()
      drawRoundedRect(0, 0, width, height, cornerRadius)
      ctx.clip()
      ctx.fillStyle = bgColor
      ctx.fillRect(0, 0, width, height)
      ctx.restore()

      const padding = 0.15
      const availableW = width * (1 - padding)
      const availableH = height * (1 - padding)
      const scale = Math.min(availableW / img.width, availableH / img.height)
      const w = img.width * scale
      const h = img.height * scale
      const x = (width - w) / 2
      const y = (height - h) / 2

      ctx.drawImage(img, x, y, w, h)

      const dataUrl = canvas.toDataURL('image/png')
      iconCache.set(cacheKey, dataUrl)
      resolve(dataUrl)
    }

    img.onerror = (err) => {
      console.error('❌ Failed to load icon image:', iconUrl, err)
      resolve(iconUrl)
    }

    img.src = iconUrl
  })
}